import { Component, Input, Output, EventEmitter, OnInit } from "@angular/core";
import { SystemUserAccount } from "../../../shared/models/system-user-account.model";
import { SystemAccount } from "../../../shared/models/system-account.model";
import { SystemAccountService } from "../../../shared/services/system-account.service";
import { SystemAccountType } from "../../../shared/enums/systemAccountType";
import { SystemAccountByEmail } from "../../../shared/models/system-account-by.email";
import { ToastrService } from "ngx-toastr";
import { AppSpinnerModule } from "../../../shared/modules/app-spinner.module";
import { tap } from "rxjs/operators";
import { AppDialogModule } from "../../../shared/components/app-dialog/app-dialog.component";
import { AccountService } from "../../../shared/services/account.service";

@Component({
    selector: 'app-user-table-display',
    templateUrl: 'user-table-display.component.html',
    styleUrls: ['user-table-display.component.scss'],
    standalone: false
})

export class UserTableDisplayComponent implements OnInit {
  @Input() userList: SystemUserAccount[] = [];
  @Input() activeTab: 'system' | 'client' = 'client';
  @Output() systemAccountDeleted = new EventEmitter<void>();
  sortAscending = true;
  sortProperty: string;
  currentUserEmail: string = '';

  accountTypes = [
    { key: SystemAccountType.ClearSignAuditor, value: "Clear Sign Auditor" },
    { key: SystemAccountType.ClearSignSupport, value: "Clear Sign Support" }
  ];

  constructor(
    private readonly systemAccountService: SystemAccountService,
    private readonly accountService: AccountService,
    private readonly toastr: ToastrService,
    private readonly spinner: AppSpinnerModule,
    private readonly dialog: AppDialogModule
  ) { }

  ngOnInit(): void {
    this.accountService.getCurrentAccount()
      .pipe(
        tap((account) => {
          this.currentUserEmail = account.emailAddress?.toLowerCase() || '';
        })
      )
      .subscribe();
  }

  get filteredUserList(): SystemUserAccount[] {
    return this.userList.filter(user => 
      user.email?.toLowerCase() !== this.currentUserEmail
    );
  }

  sortUserAccounts(property: string): SystemUserAccount[] {
    this.updateSortProperty(property);
    return this.userList.sort((a, b) => {
      const valueA = (typeof a[property] === 'string') ? a[property].toLocaleLowerCase() : a[property];
      const valueB = (typeof b[property] === 'string') ? b[property].toLocaleLowerCase() : b[property];
      if (valueA < valueB) {
        return this.sortAscending ? -1 : 1;
      } else if (valueA > valueB) {
        return this.sortAscending ? 1 : -1;
      } else {
        return 0;
      }
    });
  }

  updateSortProperty(property: string) {
    if (property === this.sortProperty) {
      this.sortAscending = !this.sortAscending;
    } else {
      this.sortAscending = true;
    }
    this.sortProperty = property;
  }

  onDelete(systemAccount: SystemAccount): void {
    this.spinner.show();

    this.systemAccountService
      .deleteSystemAccountById(systemAccount.systemAccountId)
      .pipe(
        tap(() => {
          this.spinner.hide();
          this.toastr.success("System account removed successfully.");
          this.systemAccountDeleted.emit();
        })
      )
      .subscribe({
        error: () => {
          this.spinner.hide();
          this.toastr.error("Failed to remove system account.");
        }
      });
  }

  deleteConfirmationDialog(systemAccount: SystemAccount, userName: string) {
    this.dialog.show({
      title: "Delete System Account",
      message: `Are you sure you want to delete ${userName}\'s system account?`,
      noButtonText: "Cancel",
      yesButtonText: "Delete Account",
    }).result.then((dialogResult) => {
      if (dialogResult === "yes") {
        this.onDelete(systemAccount);
      }
    });
  }

  deleteAllConfirmationDialog(user: SystemUserAccount) {
    this.dialog.show({
      title: "Delete User's System Accounts",
      message: `Are you sure you want to delete all system accounts for ${user.email}?`,
      noButtonText: "Cancel",
      yesButtonText: "Delete All",
    }).result.then((dialogResult) => {
      if (dialogResult === "yes") {
        this.onDeleteAll(user);
      }
    });
  }

  onDeleteAll(user: SystemUserAccount): void {
    this.spinner.show();
    const deletePromises = user.systemAccounts.map(account => 
      this.systemAccountService.deleteSystemAccountById(account.systemAccountId).toPromise()
    );
    
    Promise.all(deletePromises).then(() => {
      this.spinner.hide();
      this.toastr.success("All system accounts removed successfully.");
      this.systemAccountDeleted.emit();
    }).catch(() => {
      this.spinner.hide();
      this.toastr.error("Failed to remove some system accounts.");
    });
  }

  getSelectedAccountTypes(user: SystemUserAccount): string[] {
    return user.systemAccounts.map(sa => {
      const matchedType = this.accountTypes.find(
        at => at.key.toLowerCase() === sa.accountType.toLowerCase()
      );
      return matchedType ? matchedType.key : sa.accountType;
    });
  }

  onAccountTypeChange(selected: boolean, user: SystemUserAccount, accountType: string): void {
    if (selected) {
      this.addSystemAccount(user, accountType);
    } else {
      const systemAccount = user.systemAccounts.find(
        sa => sa.accountType.toLowerCase() === accountType.toLowerCase()
      );
      if (systemAccount) {
        this.onDelete(systemAccount);
      }
    }
  }

  addSystemAccount(user: SystemUserAccount, accountType: string): void {
    this.spinner.show();
    const model: SystemAccountByEmail = {
      emailAddress: user.email,
      accountTypes: [accountType as SystemAccountType]
    };
    
    this.systemAccountService.postNewSystemAccountByEmail(model)
      .pipe(
        tap(() => {
          this.spinner.hide();
          this.toastr.success("System account added successfully.");
          this.systemAccountDeleted.emit();
        })
      )
      .subscribe({
        error: () => {
          this.spinner.hide();
          this.toastr.error("Failed to add system account.");
        }
      });
  }
}
